import React, { useState, useEffect } from 'react';
import './Waitlist.css';
import { analytics } from '../../lib/analytics';
import { emailService } from '../../lib/emailService';

interface WaitlistProps {
  onSurveyTrigger?: () => void;
}

const Waitlist: React.FC<WaitlistProps> = ({ onSurveyTrigger }) => {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Load email and submission status from sessionStorage on mount
  useEffect(() => {
    const updateFromStorage = () => {
      const storedEmail = sessionStorage.getItem('userEmail');
      const isSubmitted = sessionStorage.getItem('waitlistSubmitted') === 'true';
      
      if (storedEmail) {
        setEmail(storedEmail);
      }
      
      if (isSubmitted) {
        setSubmitStatus('submitted');
      }
    };

    // Initial load
    updateFromStorage();

    // Listen for custom storage events
    const handleStorageChange = () => {
      updateFromStorage();
    };

    window.addEventListener('waitlistUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('waitlistUpdated', handleStorageChange);
    };
  }, []);

  // Save email to sessionStorage whenever it changes
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // If email changed and status was submitted, reset to idle to allow resubmit
    const storedEmail = sessionStorage.getItem('userEmail');
    if (submitStatus === 'submitted' && newEmail !== storedEmail) {
      setSubmitStatus('idle');
      setIsDuplicate(false);
      setErrorMessage('');
    }
    
    if (newEmail.trim()) {
      sessionStorage.setItem('userEmail', newEmail);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitStatus !== 'idle' && submitStatus !== 'error') return;
    
    // Set submitting state
    setSubmitStatus('submitting');
    setErrorMessage('');
    
    try {
      // Validate email
      if (!email || !email.includes('@')) {
        setErrorMessage('Please enter a valid email address');
        setSubmitStatus('error');
        return;
      }

      analytics.trackFormSubmit('bottom_waitlist_form', { email });
      
      const result = await analytics.createUser(email, 'waitlist');
      
      if (!result.success) {
        setErrorMessage('Failed to save your email. Please try again.');
        setSubmitStatus('error');
        analytics.trackInteraction('waitlist_error', 'bottom_waitlist_form');
        return;
      }
      
      if (result.isDuplicate) {
        console.log('User already on waitlist');
        sessionStorage.setItem('userEmail', email);
        sessionStorage.setItem('waitlistSubmitted', 'true');
        
        window.dispatchEvent(new CustomEvent('waitlistUpdated'));
        
        setIsDuplicate(true);
        setSubmitStatus('submitted');
        return;
      }
      
      setIsDuplicate(false);
      
      console.log('User created successfully in Supabase');
      
      sessionStorage.setItem('userEmail', email);
      sessionStorage.setItem('waitlistSubmitted', 'true');
      
      window.dispatchEvent(new CustomEvent('waitlistUpdated'));
      
      setSubmitStatus('submitted');
      
      try {
        const emailSent = await emailService.sendWelcomeEmail(email);
        if (emailSent) {
          console.log('Welcome email sent successfully via SendGrid');
        } else {
          console.warn('Email sending failed, but user was saved');
        }
      } catch (emailError) {
        console.error('Email sending error:', emailError);
      }
      
      analytics.trackInteraction('waitlist_success', 'bottom_waitlist_form');
      
      if (onSurveyTrigger) {
        setTimeout(() => {
          onSurveyTrigger();
        }, 1000);
      }
    } catch (error) {
      console.error('Error submitting waitlist:', error);
      setErrorMessage('Something went wrong. Please try again.');
      setSubmitStatus('error');
      
      // Track error (only if consent given)
      analytics.trackInteraction('waitlist_error', 'bottom_waitlist_form', { 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    }
  };

  return (
    <section className="waitlist-section" id="waitlist">
      <div className="features-container">
        <img src="/Logo Pack-30.png" alt="Archaas Logo" className="waitlist-logo" />
        <h2 className="waitlist-title">Get Early Access</h2>
        <p className="waitlist-subtitle">
        We are currently working with select architects. Sign up to get early access.
        </p>
        <form className="waitlist-form" onSubmit={handleSubmit}>
          <div className="waitlist-input-container">
            <input
              type="email"
              placeholder="Your Email"
              value={email}
              onChange={handleEmailChange}
              className="waitlist-input"
              required
              disabled={submitStatus === 'submitting'}
            />
            <button 
              type="submit" 
              className={`waitlist-button ${submitStatus === 'submitting' ? 'waitlist-button--loading' : ''}`}
              disabled={submitStatus === 'submitting'}
            >
              {submitStatus === 'idle' && 'Join Waitlist'}
              {submitStatus === 'error' && 'Try Again'}
              {submitStatus === 'submitting' && (
                <>
                  <span className="loading-spinner"></span>
                  Joining...
                </>
              )}
              {submitStatus === 'submitted' && (
                <>
                  <span className="success-checkmark">✓</span>
                  Thank you!
                </>
              )}
            </button>
          </div>
          {submitStatus === 'submitted' ? (
            <p className="waitlist-success-message" style={{ color: '#10b981' }}>
              {isDuplicate ? "You're already on our waitlist!" : "Welcome to the waitlist! We'll be in touch soon."}
            </p>
          ) : submitStatus === 'error' ? (
            <p className="waitlist-success-message" style={{ color: '#ef4444' }}>{errorMessage}</p>
          ) : (
            <p className="waitlist-privacy">We will never share your information with anyone.</p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Waitlist; 